package diana.soleil.design.iraniancanadienbazzar.core.designsystem.component

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.style.TextOverflow
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme

/**
 * Base surface card (L1): white, 16px radius, hairline border, soft elevation.
 * [elevation] overrides the default L1 shadow (pass `AppTheme.elevation.level2` for cards that
 * must visibly float, e.g. the category grid).
 */
@Composable
fun AppCard(
    modifier: Modifier = Modifier,
    onClick: (() -> Unit)? = null,
    elevation: androidx.compose.ui.unit.Dp? = null,
    content: @Composable () -> Unit,
) {
    val colors = CardDefaults.cardColors(
        containerColor = AppTheme.colors.surface,
        contentColor = AppTheme.colors.onSurface,
    )
    val cardElevation = CardDefaults.cardElevation(defaultElevation = elevation ?: AppTheme.elevation.level1)
    if (onClick != null) {
        Card(onClick = onClick, modifier = modifier, shape = AppTheme.shapes.card, colors = colors, elevation = cardElevation) { content() }
    } else {
        Card(modifier = modifier, shape = AppTheme.shapes.card, colors = colors, elevation = cardElevation) { content() }
    }
}

/**
 * Business listing card (docs/DESIGN_SYSTEM.md). Pass the cover/logo via [image] (a placeholder is used
 * until image loading is wired). [actions] slot hosts Call/Directions/Chat/View buttons.
 */
@Composable
fun BusinessCard(
    name: String,
    category: String,
    rating: Float,
    reviewCount: Int,
    cityAddress: String,
    modifier: Modifier = Modifier,
    verified: Boolean = false,
    sponsoredLabel: String? = null,
    verifiedContentDescription: String? = null,
    onClick: (() -> Unit)? = null,
    image: @Composable (() -> Unit)? = null,
    actions: @Composable (() -> Unit)? = null,
) {
    AppCard(modifier = modifier.fillMaxWidth(), onClick = onClick) {
        Column {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .aspectRatio(16f / 9f)
                    .clip(AppTheme.shapes.card)
                    .background(AppTheme.colors.outlineVariant),
                contentAlignment = Alignment.TopEnd,
            ) {
                if (image != null) image()
                if (sponsoredLabel != null) {
                    SponsoredBadge(label = sponsoredLabel, modifier = Modifier.padding(AppTheme.spacing.sm))
                }
            }
            Column(modifier = Modifier.padding(AppTheme.spacing.lg)) {
                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.xs)) {
                    Text(
                        text = name,
                        style = AppTheme.typography.headlineMd,
                        color = AppTheme.colors.onSurface,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis,
                        modifier = Modifier.weight(1f, fill = false),
                    )
                    if (verified) VerificationBadge(contentDescription = verifiedContentDescription)
                }
                Text(
                    text = category,
                    style = AppTheme.typography.bodySm,
                    color = AppTheme.colors.onSurfaceVariant,
                    modifier = Modifier.padding(top = AppTheme.spacing.xs),
                )
                RatingStars(
                    rating = rating,
                    reviewCount = reviewCount,
                    modifier = Modifier.padding(top = AppTheme.spacing.sm),
                )
                Text(
                    text = cityAddress,
                    style = AppTheme.typography.bodySm,
                    color = AppTheme.colors.onSurfaceVariant,
                    modifier = Modifier.padding(top = AppTheme.spacing.xs),
                )
                if (actions != null) {
                    Box(modifier = Modifier.padding(top = AppTheme.spacing.md)) { actions() }
                }
            }
        }
    }
}

/** Category tile: icon slot + name + business count. */
@Composable
fun CategoryCard(
    name: String,
    count: Int,
    modifier: Modifier = Modifier,
    onClick: (() -> Unit)? = null,
    icon: @Composable (() -> Unit)? = null,
) {
    AppCard(modifier = modifier, onClick = onClick) {
        Column(
            modifier = Modifier.padding(AppTheme.spacing.lg),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm),
        ) {
            Box(
                modifier = Modifier
                    .size(AppTheme.sizes.categoryTile)
                    .clip(AppTheme.shapes.tile)
                    .background(AppTheme.colors.categoryChip),
                contentAlignment = Alignment.Center,
            ) {
                if (icon != null) icon()
            }
            Text(name, style = AppTheme.typography.labelMd, color = AppTheme.colors.onSurface)
            Text("$count", style = AppTheme.typography.labelSm, color = AppTheme.colors.onSurfaceVariant)
        }
    }
}
